import React, { useEffect, useState } from 'react';
import { SearchIcon, CancelIcon, Theme, FilterIcon, Button } from '@spoiler-alert/ui-library';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    order: 0,
  },
  fullWidthSearch: {
    width: 500,
    marginRight: 10,
  },
  smallScreenSearch: {
    width: 357,
    marginRight: 8,
  },
});

const useSearchStyles = createUseStyles({
  search__container: {
    position: 'relative',
  },
  search__input: {
    position: 'relative',
    border: `1px solid ${Theme.grey80}`,
    borderRadius: Theme.borderRadius,
    padding: '9px 43px',
    zIndex: 1,
    fontSize: '0.875rem',
    lineHeight: '1rem',
    width: '100%',
    '&::placeholder': {
      color: Theme.grey80,
      fontWeight: 300,
      fontFamily: 'Roboto',
    },
  },
  search__icon: {
    position: 'absolute',
    height: '20px',
    width: '20px',
    fill: Theme.checkboxBorderColor,
    top: '22%',
    left: '16px',
    zIndex: 2,
  },
  cancel__icon: {
    position: 'absolute',
    visibility: 'hidden',
    height: '16px',
    width: '16px',
    fill: '#99A1B0',
    top: '7px',
    right: '16px',
    zIndex: 3,
    opacity: 0,
    transitionDuration: '200ms',
  },
  'cancel__icon--visible': {
    cursor: 'pointer',
    visibility: 'visible',
    opacity: 1,
  },
});

// Module-level no-op so the useEffect dependency array keeps a stable identity when onChange is omitted;
// the JSDoc type keeps the inferred prop signature permissive for TypeScript callers
/** @type {(...args: any[]) => void} */
const noop = () => {};

const SearchInput = ({ id, className, onChange = noop, disabled, placeholder, searchText }) => {
  const classes = useSearchStyles();
  const [localValue, setLocalValue] = useState(searchText || '');
  const showCancel = localValue.trim() !== '';

  useEffect(() => {
    setLocalValue(searchText || '');
  }, [searchText]);

  useEffect(() => {
    if (disabled && localValue.length > 0) {
      setLocalValue('');
      onChange('');
    }
  }, [disabled, localValue, onChange]);

  const handleChange = (ev) => {
    setLocalValue(ev.target.value);
    onChange(ev.target.value);
  };

  const clear = () => {
    setLocalValue('');
    onChange('');
  };

  const cancelClass = `${classes.cancel__icon}${showCancel ? ` ${classes['cancel__icon--visible']}` : ''}`;
  const containerClass = `${classes.search__container}${className ? ` ${className}` : ''}`;
  return (
    <div className={containerClass} data-element="search-container" id={id}>
      <SearchIcon className={classes.search__icon} />
      <input
        disabled={disabled}
        type="text"
        value={localValue}
        data-element="search-input"
        className={classes.search__input}
        placeholder={placeholder || 'Search'}
        onChange={handleChange}
        id={`${id}-search-input`}
      />
      <div className={cancelClass} data-element="search-clear" onClick={clear}>
        <CancelIcon />
      </div>
    </div>
  );
};

SearchInput.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  searchText: PropTypes.string,
};

const TrucklaneSearch = ({ handleSearch, toggleFilters, filtersAppliedCount, fullScreen, searchText }) => {
  const classes = useStyles();
  return (
    <div className={classes.row}>
      <SearchInput
        id={'text-search'}
        onChange={handleSearch}
        searchText={searchText}
        className={fullScreen ? classes.fullWidthSearch : classes.smallScreenSearch}
      />
      <Button icon={FilterIcon} secondary onClick={toggleFilters} filter={filtersAppliedCount !== 0}>
        {filtersAppliedCount > 0 ? `${filtersAppliedCount} ${pluralize('Filter', filtersAppliedCount)}` : 'Filter'}
      </Button>
    </div>
  );
};

TrucklaneSearch.propTypes = {
  handleSearch: PropTypes.func.isRequired,
  toggleFilters: PropTypes.func.isRequired,
  filtersAppliedCount: PropTypes.number.isRequired,
  fullScreen: PropTypes.bool,
  searchText: PropTypes.string,
};

export default TrucklaneSearch;
